import {
  Building2,
  Check,
  Clipboard,
  Sparkles,
  UtensilsCrossed,
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { buildPairingPrompt, groupWinesForPairing } from "../lib/wine";
import type { CellarPlaceRecord, Wine } from "../types";
import { Modal } from "./Modal";

interface PairingAdviceModalProps {
  open: boolean;
  onClose: () => void;
}

export function PairingAdviceModal({ open, onClose }: PairingAdviceModalProps) {
  const [request, setRequest] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [places, setPlaces] = useState<CellarPlaceRecord[]>([]);
  const [wines, setWines] = useState<Wine[]>([]);
  const [prompt, setPrompt] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    void Promise.all([api.listWines("en_stock"), api.listPlaces()])
      .then(([availableWines, availablePlaces]) => {
        if (cancelled) return;
        setWines(availableWines);
        setPlaces(availablePlaces);
        setPlaceId((current) =>
          availablePlaces.some((place) => place.id === current) ? current : "",
        );
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(
          caught instanceof Error
            ? caught.message
            : "Impossible de charger les bouteilles disponibles.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const selectedPlace = useMemo(
    () => places.find((place) => place.id === placeId) ?? null,
    [placeId, places],
  );

  const close = () => {
    setPreviewing(false);
    setCopied(false);
    setCopyError("");
    setError("");
    onClose();
  };

  const prepare = (event: FormEvent) => {
    event.preventDefault();
    if (!request.trim() || !selectedPlace) {
      setError("Indiquez un plat ou une envie et choisissez un lieu.");
      return;
    }
    const available = groupWinesForPairing(
      wines,
      selectedPlace.nom,
      selectedPlace.id,
    );
    if (!available.length) {
      setError(
        `Aucune bouteille n’est disponible à ${selectedPlace.nom}. Choisissez un autre lieu ou ajoutez du stock.`,
      );
      return;
    }
    setPrompt(
      buildPairingPrompt(wines, request, selectedPlace.nom, selectedPlace.id),
    );
    setCopied(false);
    setCopyError("");
    setError("");
    setPreviewing(true);
  };

  const copy = async () => {
    try {
      await copyText(prompt);
      setCopied(true);
      setCopyError("");
      window.setTimeout(() => setCopied(false), 2500);
    } catch (caught) {
      setCopyError(caught instanceof Error ? caught.message : "La copie a échoué.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={previewing ? "Votre demande d’accord" : "Conseil d’accord"}
      description={
        previewing
          ? "Aucune donnée n’a été envoyée. Vérifiez puis copiez ce prompt dans l’IA de votre choix."
          : "Préparez une demande uniquement avec les bouteilles disponibles dans le lieu choisi."
      }
      size={previewing ? "large" : "medium"}
    >
      {previewing ? (
        <>
          <div className="prompt-heading">
            <div>
              <span className="success-dot"><Check size={14} aria-hidden="true" /></span>
              <strong>Prompt prêt à copier</strong>
            </div>
            <button type="button" onClick={() => setPreviewing(false)}>
              Modifier ma demande
            </button>
          </div>
          <pre className="prompt-preview">{prompt}</pre>
          {copied ? (
            <p className="copy-confirmation" role="status">
              <Check size={16} aria-hidden="true" /> Prompt copié dans le presse-papiers.
            </p>
          ) : null}
          {copyError ? <p className="form-error" role="alert">{copyError}</p> : null}
          <div className="modal-actions">
            <button className="button button-ghost" type="button" onClick={close}>
              Fermer
            </button>
            <button className="button button-primary" type="button" onClick={() => void copy()}>
              <Clipboard size={18} aria-hidden="true" /> {copied ? "Copié" : "Copier le prompt"}
            </button>
          </div>
        </>
      ) : (
        <form className="pairing-modal-form" onSubmit={prepare}>
          <div className="pairing-modal-intro" aria-hidden="true">
            <span className="pairing-icon"><UtensilsCrossed size={20} /></span>
          </div>
          <label className="field pairing-request">
            <span>Quel plat ou quelle envie ? <b>*</b></span>
            <input
              value={request}
              onChange={(event) => {
                setRequest(event.target.value);
                setError("");
              }}
              placeholder="Poisson grillé, dîner d’été ou envie d’un rouge léger"
              required
              autoFocus
            />
          </label>
          <label className="field">
            <span>Lieu des bouteilles <b>*</b></span>
            <span className="select-with-icon">
              <Building2 size={17} aria-hidden="true" />
              <select
                value={placeId}
                onChange={(event) => {
                  setPlaceId(event.target.value);
                  setError("");
                }}
                required
                disabled={loading || places.length === 0}
              >
                <option value="">Choisir un lieu…</option>
                {places.map((place) => (
                  <option key={place.id} value={place.id}>{place.nom}</option>
                ))}
              </select>
            </span>
          </label>
          {loading ? <p className="pairing-loading" role="status">Chargement de la cave…</p> : null}
          {!loading && places.length === 0 && !error ? (
            <p className="pairing-error" role="status">
              Ajoutez d’abord un lieu et un emplacement pour préparer un conseil.
            </p>
          ) : null}
          {error ? <p className="pairing-error" role="alert">{error}</p> : null}
          <div className="modal-actions">
            <button className="button button-ghost" type="button" onClick={close}>
              Annuler
            </button>
            <button
              className="button pairing-submit"
              type="submit"
              disabled={loading || !request.trim() || !placeId}
            >
              <Sparkles size={18} aria-hidden="true" /> Préparer mon conseil
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
